// Store schema (DDL), SQLite backend, and openStore() (ARCHITECTURE.md §12).
//
// The DDL constant is the verbatim schema from §12.3 (with IF NOT EXISTS so it
// is idempotent). openStore() prefers the better-sqlite3 backend and falls back
// to a pure in-memory backend when the optional peer dependency is absent.

import { mkdirSync } from "node:fs";
import { dirname, join } from "node:path";

import { createMemoryBackend } from "./memory.js";
import type { FileRow, StoreBackend, StoreHandle, StoreOptions } from "./types.js";

export const SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// DDL (§12.3)
// ---------------------------------------------------------------------------

export const SCHEMA_DDL = `
-- Schema version tracking
CREATE TABLE IF NOT EXISTS schema_version (
  version     INTEGER NOT NULL
);

-- Analyzed source files
CREATE TABLE IF NOT EXISTS files (
  id          INTEGER PRIMARY KEY,
  path        TEXT NOT NULL UNIQUE,
  hash        TEXT NOT NULL,
  language    TEXT NOT NULL,
  line_count  INTEGER NOT NULL,
  analyzer_id TEXT NOT NULL,
  analyzed_at TEXT NOT NULL
);

-- Key-value metadata (hashes for invalidation, timestamps)
CREATE TABLE IF NOT EXISTS metadata (
  key         TEXT PRIMARY KEY,
  value       TEXT NOT NULL
);
`;

// ---------------------------------------------------------------------------
// Minimal better-sqlite3 surface (no @types dependency)
// ---------------------------------------------------------------------------

interface RawStatement {
  run(...params: unknown[]): { lastInsertRowid: number | bigint; changes: number };
  get(...params: unknown[]): unknown;
  all(...params: unknown[]): unknown[];
}

interface RawDatabase {
  pragma(source: string, options?: { simple?: boolean }): unknown;
  exec(source: string): unknown;
  prepare(source: string): RawStatement;
  transaction<F extends (...args: unknown[]) => unknown>(fn: F): F;
  close(): void;
}

type RawDatabaseCtor = new (path: string) => RawDatabase;

// ---------------------------------------------------------------------------
// SQLite backend
// ---------------------------------------------------------------------------

function createSqliteBackend(db: RawDatabase): StoreBackend {
  return {
    getSchemaVersion(): number {
      const row = db
        .prepare("SELECT version FROM schema_version LIMIT 1")
        .get() as { version: number } | undefined;
      return row?.version ?? 0;
    },

    getMetadata(key: string): string | undefined {
      const row = db
        .prepare("SELECT value FROM metadata WHERE key = ?")
        .get(key) as { value: string } | undefined;
      return row?.value;
    },
    setMetadata(key: string, value: string): void {
      db.prepare(
        "INSERT INTO metadata (key, value) VALUES (?, ?) " +
          "ON CONFLICT(key) DO UPDATE SET value = excluded.value",
      ).run(key, value);
    },

    insertOrReplaceFile(file: Omit<FileRow, "id">): number {
      const info = db
        .prepare(
          "INSERT OR REPLACE INTO files " +
            "(path, hash, language, line_count, analyzer_id, analyzed_at) " +
            "VALUES (?, ?, ?, ?, ?, ?)",
        )
        .run(
          file.path,
          file.hash,
          file.language,
          file.line_count,
          file.analyzer_id,
          file.analyzed_at,
        );
      return Number(info.lastInsertRowid);
    },
    deleteFileByPath(path: string): boolean {
      const info = db.prepare("DELETE FROM files WHERE path = ?").run(path);
      return info.changes > 0;
    },
    getFileByPath(path: string): FileRow | undefined {
      return db.prepare("SELECT * FROM files WHERE path = ?").get(path) as
        | FileRow
        | undefined;
    },
    getFileById(id: number): FileRow | undefined {
      return db.prepare("SELECT * FROM files WHERE id = ?").get(id) as
        | FileRow
        | undefined;
    },
    getAllFiles(): FileRow[] {
      return db.prepare("SELECT * FROM files").all() as FileRow[];
    },

    transaction<T>(fn: () => T): T {
      return db.transaction(fn as (...args: unknown[]) => unknown)() as T;
    },

    close(): void {
      db.close();
    },
  };
}

// ---------------------------------------------------------------------------
// openStore()
// ---------------------------------------------------------------------------

async function loadSqliteCtor(): Promise<RawDatabaseCtor | null> {
  try {
    // Non-literal specifier: better-sqlite3 is an optional peer dependency that
    // may be absent at build time, so it must not be statically resolved.
    const specifier = "better-sqlite3";
    const mod = (await import(specifier)) as {
      default?: RawDatabaseCtor;
    };
    return (mod.default ?? (mod as unknown as RawDatabaseCtor)) ?? null;
  } catch {
    return null;
  }
}

function resolveDbPath(options?: StoreOptions): string {
  const explicit = options?.dbPath ?? process.env.AGENT_ANALYZER_STORE;
  if (explicit) return explicit;
  return join(process.cwd(), ".analyzer", "store.db");
}

function initializeSqlite(db: RawDatabase): void {
  // PRAGMA settings (§12.8). WAL is a no-op for :memory: databases.
  db.pragma("journal_mode = WAL");
  db.pragma("synchronous = NORMAL");
  db.pragma("foreign_keys = ON");
  db.pragma("cache_size = -8000");
  // Parallel MCP tool calls (e.g. run-tests + evaluate-implementation) share one DB file.
  db.pragma("busy_timeout = 30000");

  db.exec(SCHEMA_DDL);

  const existing = db
    .prepare("SELECT version FROM schema_version LIMIT 1")
    .get() as { version: number } | undefined;
  if (!existing) {
    db.prepare("INSERT INTO schema_version (version) VALUES (?)").run(
      SCHEMA_VERSION,
    );
  }
}

function createMemoryHandle(): StoreHandle {
  return {
    kind: "memory",
    dbPath: null,
    backend: createMemoryBackend(),
    raw: null,
    close() {
      this.backend.close();
    },
  };
}

/**
 * Open the analyzer store. Uses better-sqlite3 when available, otherwise falls
 * back to an in-memory backend (§12.2).
 *
 * Environment:
 *   AGENT_ANALYZER_NO_STORE=1   in-memory only (`:memory:` when SQLite present)
 *   AGENT_ANALYZER_STORE=<path> custom database path
 */
export async function openStore(options?: StoreOptions): Promise<StoreHandle> {
  const noStore = process.env.AGENT_ANALYZER_NO_STORE === "1";

  const Ctor = await loadSqliteCtor();
  if (!Ctor) {
    console.warn(
      "[artifact-contracts-analyzer] better-sqlite3 is not installed; " +
        "using an in-memory store (data will not persist between runs).",
    );
    return createMemoryHandle();
  }

  const path = noStore ? ":memory:" : resolveDbPath(options);
  if (path !== ":memory:") {
    mkdirSync(dirname(path), { recursive: true });
  }

  const db = new Ctor(path);
  initializeSqlite(db);

  return {
    kind: "sqlite",
    dbPath: path === ":memory:" ? null : path,
    backend: createSqliteBackend(db),
    raw: db,
    close() {
      db.close();
    },
  };
}
